/**
 * The Notification Prompt component is responsible for alerting and signing up for web push notifications
 * 
 * Checks if the current device is eligible for push notifications via the web (all desktop, only android / chrome for mobile)
 * If not eligibale, then go away
 * If eligiable, we will show a prompt for the user to click to get notifications
 * When clicked, a modal should appear showing the benifits with a sign me up or a decline
 * If declined, we will store the answer in local storage
 * Re-prompt should occur after 3 days, then again after 7 days.  Then again after 30 days
 * 
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../constants/Colorsv2';
import { Text } from '../globalComponents/NativeOverrides';
import { PublicPlayerProps } from '../store/types/auth_svc';
import { getPushNotificationStatus, getPushPermission, initializePushNotifications, isPushNotificationSupported } from '../utils/notifications/webNotification';
import HelpCallout from '../help/HelpCallout';
import { Icon } from 'react-native-elements';
import { player_getPushSubByPlayerIdAndDvcId, player_getPushSubsByPlayerId, player_savePushSubscription, player_updatePushSubscription } from '../store/actionsv1/player';
import { isPWAInstalled } from '../utils/analytics/segment';
import { BEEventApi } from 'be-components';


const NotificationPrompt = ({}:any) => {
    const [ show_notification_prompt, setShowNotificationPrompt ] = useState(false);

    const dispatch = useDispatch();
    const player:PublicPlayerProps | undefined = useSelector((state:any) => state.player.player, (left, right) => left?.player_id == right?.player_id);
    const analytic_tokens = useSelector((state:any) => state.util.analytic_tokens, (left, right) => left == right);
    const { distinct_id } = analytic_tokens;
    const help_action_select: undefined | { client_native_id:string, selected_action_type: string } = useSelector((state:any) => state.util.help_action_select, (left, right) => {
        if(left?.client_native_id != right?.client_native_id){ return false }
        if(left?.help_action_select != right?.help_action_select){ return false }
        return true
    });

    const notifications_supported = isPushNotificationSupported()
    /**
     * When a player is logged in - we first check if notifications are supported
     * if they are, then we can check to see if they have them already enabled
     */
    useEffect(() => {
        if(!notifications_supported){ return } //Don't try and get any push subscriptions since they are not supported
        if(!player?.player_id){ return } //Don't get any push subscriptions until there is a player logged in
        checkPushStatus(player.player_id)
        //Ok - they are granted! Now, lets save the notification subscription to the server
    },[player?.player_id])

    useEffect(() => {
        if(!help_action_select){ return }
        if(help_action_select.client_native_id != 't2_notifications'){ return }
        handleNotificationResponse(help_action_select.selected_action_type)
    },[help_action_select])


    /**
     * 
     */
    const checkPushStatus = async(player_id:string) => {
        //First - get permission status
        const push_permission = getPushPermission()
        if(push_permission !== 'granted'){ return setShowNotificationPrompt(true) } //If they do not give permission, then we need to show the prompt
        

        //Get the existing subscription
        let existing_subscription = await player_getPushSubByPlayerIdAndDvcId(player_id, distinct_id)
        let push_sub = await getPushNotificationStatus(player_id, distinct_id)
        //If there is a push sub, that means the client is allowing push notifications, so we need to make sure the server push sub is still active
        if(push_sub){
            //We dont have a server push sub - so lets save it
            if(!existing_subscription){ return await dispatch(player_savePushSubscription(push_sub)) }
            //There is a server push sub - so now lets update it if we need to
            else {
                //Lets update the subscription if there is a change
                if(push_sub.auth_key != existing_subscription.auth_key || push_sub.p256dh_key != existing_subscription.p256dh_key){
                    return await dispatch(player_updatePushSubscription({ ...existing_subscription, auth_key: push_sub.auth_key, p256dh_key:push_sub.p256dh_key }))
                }
            }
        } else {
            //There was no push sub!
            return setShowNotificationPrompt(true)
        }
    }



    const handleNotificationResponse = async(response: string) => {
        if(!player){ return }
        if(response === 'accept'){
            const device_status = await initializePushNotifications()
            if(device_status !== 'granted'){ return alert('Uh oh! It looks like your browser is preventing notifications. Please enable them in your settings first.') }
            let push_sub = await getPushNotificationStatus(player.player_id, distinct_id)
            if(!push_sub){ return }
            await dispatch(player_savePushSubscription(push_sub))
            setShowNotificationPrompt(false);
        }
    }

    if(!isPWAInstalled()){ return <></> }
    if(!player){ return <></> } //Do nothing if there is no player logged in
    if(!notifications_supported){ return <></> } //Do nothing if notifications are not supported
    if(!show_notification_prompt){ return <></> }
    return (
        <HelpCallout client_native_id='t2_notifications' pressed={() => {
            BEEventApi.saveEvent({
                event_name: 'viewed_notification_prompt',
                event_data: {
    
                },
                level:1
            })
        }} style={{ 
                backgroundColor:Colors.highlights.highlight400Faded, flexDirection:'row', alignItems:'center', padding:5, paddingTop:10, paddingBottom:10, borderRadius:20 
            }}>
                <Icon name='bell' type='feather' color={Colors.shades.white} size={14}/>
                <Text size={12} color={Colors.shades.white} textAlign='center' weight='bold' style={{marginLeft:3}}>Get Notified!</Text>
        </HelpCallout>
    )
}


export default NotificationPrompt